/**
 * useProfile hooks
 * React Query hooks for profile management
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { 
  getUserProfile, 
  getNotificationPreferences,
  updateUserProfile, 
  updateNotificationPreferences,
  uploadProfilePhoto,
  type NotificationPreferencesPayload,
  type UpdateProfileRequest,
  type UserProfile 
} from '../api/profileApi';

/**
 * Hook to fetch current user profile.
 *
 * `enabled` lets callers skip the request for guests — /users/profile/me is an
 * authenticated endpoint, so firing it anonymously 401s and bounces to /login.
 */
export const useUserProfile = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['profile'],
    queryFn: getUserProfile,
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: options?.enabled ?? true,
  });
};

export const useNotificationPreferences = () => {
  return useQuery({
    queryKey: ['profile', 'notification-preferences'],
    queryFn: getNotificationPreferences,
    staleTime: 1000 * 60 * 5,
  });
};

export const useUpdateNotificationPreferences = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: NotificationPreferencesPayload) => updateNotificationPreferences(data),
    onSuccess: (preferences: NotificationPreferencesPayload) => {
      queryClient.setQueryData(['profile', 'notification-preferences'], preferences);
      queryClient.invalidateQueries({ queryKey: ['profile', 'notification-preferences'] });
    },
  });
};

/**
 * Hook to update user profile
 * Invalidates profile cache on success
 */
export const useUpdateProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateProfileRequest) => updateUserProfile(data),
    onSuccess: (updatedProfile: UserProfile) => {
      // Update the profile cache
      queryClient.setQueryData(['profile'], updatedProfile);
      
      // Invalidate to refetch and sync with server
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      
      // If profileCompleted changed, also invalidate auth queries
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });
};

/**
 * Hook to upload profile photo
 * Backend handles S3 upload and updates profile in one operation
 * Invalidates profile cache on success
 */
export const useUploadProfilePhoto = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => uploadProfilePhoto(file),
    onSuccess: (updatedProfile: UserProfile) => {
      // Update the profile cache with the uploaded photo
      queryClient.setQueryData(['profile'], updatedProfile);
      
      // Invalidate to refetch and sync with server
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      
      // If profileCompleted changed, also invalidate auth queries
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });
};

